import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export type OpsTatPath = "all" | "custom" | "stock";

export type OpsTatStatus = "ok" | "warn" | "bad" | null;

export type OpsTatRecordScope = "measured" | "open" | "all";

export type OpsTatSortDir = "asc" | "desc";

export interface OpsTatPercentiles {
  mean: number;
  p50: number;
  p90: number;
}

export interface OpsTatMetaRange {
  start: string | null;
  end: string | null;
  label: string | null;
}

export interface OpsTatMeta {
  range: OpsTatMetaRange;
  generated_at: string;
  path?: OpsTatPath;
  filters_applied: number;
  include_running?: boolean;
  metricId?: string;
}

export interface OpsTatSort {
  column: string;
  dir: OpsTatSortDir;
}

/** Shared TAT JSON body. Never include `category` — a non-empty array is 400. */
export interface OpsAnalyticsFilters {
  start_date?: string;
  end_date?: string;
  order_id?: number[];
  order_type?: number[];
  project_poc_id?: number[];
  sourcing_poc_id?: number[];
  vendor_id?: number[];
  path?: OpsTatPath;
  project_status?: boolean | null;
  excluded_orders_from_sourcing?: boolean;
  include_running?: boolean;
}

export interface OpsTatAppliedFilters {
  start_date: Dayjs;
  end_date: Dayjs;
  order_id: number[];
  order_type: number[];
  project_poc_id: number[];
  sourcing_poc_id: number[];
  vendor_id: number[];
  path: OpsTatPath;
  project_status: boolean | null;
  excluded_orders_from_sourcing: boolean;
  include_running: boolean;
}

export interface OpsTatDrilldownQuery {
  page: number;
  page_size: number;
  record_scope: OpsTatRecordScope;
  sort?: OpsTatSort;
}

export const OPS_INWARD_DEV_DEFAULT_PAGE_SIZE = 100;

export const OPS_INWARD_DEV_VENDOR_PAGE_SIZE = 10;

/** Inward-dev API defaults to 25 if omitted/invalid. `vendor_dev` opens at 10. */
export const resolveOpsInwardDevPageSize = (
  pageSize?: number,
  metricId?: string,
): number => {
  if (pageSize === 10 || pageSize === 50 || pageSize === 100) {
    return pageSize;
  }
  return metricId === "vendor_dev"
    ? OPS_INWARD_DEV_VENDOR_PAGE_SIZE
    : OPS_INWARD_DEV_DEFAULT_PAGE_SIZE;
};

export const OPS_TAT_FILTER_TITLES = {
  order_id: "Order IDs",
  order_type: "Order type",
  project_poc_id: "Project POC",
  sourcing_poc_id: "Sourcing POC",
  vendor_id: "Vendor",
  project_status: "Project status",
  excluded_orders_from_sourcing: "Excluded from sourcing",
  include_running: "Include running",
} as const;

export const defaultOpsTatAppliedFilters = (): OpsTatAppliedFilters => ({
  start_date: dayjs().subtract(29, "day").startOf("day"),
  end_date: dayjs().endOf("day"),
  order_id: [],
  order_type: [],
  project_poc_id: [],
  sourcing_poc_id: [],
  vendor_id: [],
  path: "all",
  project_status: null,
  excluded_orders_from_sourcing: false,
  include_running: false,
});

export const defaultOpsTatDrilldownQuery = (): OpsTatDrilldownQuery => ({
  page: 1,
  page_size: OPS_INWARD_DEV_DEFAULT_PAGE_SIZE,
  record_scope: "measured",
});

/** Local calendar day → UTC `YYYY-MM-DD HH:mm:ss`. Start of day or end of day. */
export const toOpsUtcBound = (
  localDay: Dayjs,
  bound: "start" | "end" = "start",
): string =>
  (bound === "end" ? localDay.endOf("day") : localDay.startOf("day"))
    .utc()
    .format("YYYY-MM-DD HH:mm:ss");

/** Local calendar days → UTC `YYYY-MM-DD HH:mm:ss`. End date is inclusive (end of day). */
export const toOpsAnalyticsFilters = (
  applied: OpsTatAppliedFilters,
): OpsAnalyticsFilters => {
  try {
    const body: OpsAnalyticsFilters = {
      start_date: toOpsUtcBound(applied.start_date, "start"),
      end_date: toOpsUtcBound(applied.end_date, "end"),
      path: applied.path,
      excluded_orders_from_sourcing: applied.excluded_orders_from_sourcing,
      include_running: applied.include_running,
    };

    if (applied.order_id.length > 0) {
      body.order_id = applied.order_id;
    }

    if (applied.order_type.length > 0) {
      body.order_type = applied.order_type;
    }
    if (applied.project_poc_id.length > 0) {
      body.project_poc_id = applied.project_poc_id;
    }
    if (applied.sourcing_poc_id.length > 0) {
      body.sourcing_poc_id = applied.sourcing_poc_id;
    }
    if (applied.vendor_id.length > 0) {
      body.vendor_id = applied.vendor_id;
    }
    if (applied.project_status !== null) {
      body.project_status = applied.project_status;
    }

    return body;
  } catch (error) {
    throw error;
  }
};
